import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';

// Main Layout Container
export interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: 'default' | 'centered' | 'sidebar';
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
}

const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  ({ className, children, variant = 'default', maxWidth = 'full', ...props }, ref) => {
    const getLayoutClasses = () => {
      const baseClasses = 'min-h-screen';
      
      const variantClasses = {
        default: 'w-full',
        centered: 'flex items-center justify-center',
        sidebar: 'flex',
      };

      const maxWidthClasses = {
        sm: 'max-w-sm mx-auto',
        md: 'max-w-md mx-auto',
        lg: 'max-w-lg mx-auto',
        xl: 'max-w-xl mx-auto',
        '2xl': 'max-w-2xl mx-auto',
        full: 'w-full',
      };

      return cn(
        baseClasses,
        variantClasses[variant],
        maxWidth !== 'full' ? maxWidthClasses[maxWidth] : '',
        className
      );
    };

    return (
      <div ref={ref} className={getLayoutClasses()} {...props}>
        {children}
      </div>
    );
  }
);

Layout.displayName = 'Layout';

// Header Component
export interface HeaderProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  sticky?: boolean;
  bordered?: boolean;
}

const Header = forwardRef<HTMLElement, HeaderProps>(
  ({ className, children, sticky = false, bordered = true, ...props }, ref) => {
    return (
      <header
        ref={ref}
        className={cn(
          'w-full bg-white dark:bg-gray-900 z-50',
          sticky && 'sticky top-0',
          bordered && 'border-b border-gray-200',
          className
        )}
        {...props}
      >
        {children}
      </header>
    );
  }
);

Header.displayName = 'Header';

// Main Content Container
export interface MainProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  padding?: boolean;
}

const Main = forwardRef<HTMLElement, MainProps>(
  ({ className, children, padding = true, ...props }, ref) => {
    return (
      <main
        ref={ref}
        className={cn(
          'flex-1 w-full',
          padding && 'p-4 md:p-6 lg:p-8',
          className
        )}
        {...props}
      >
        {children}
      </main>
    );
  }
);

Main.displayName = 'Main';

// Sidebar Component
export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  position?: 'left' | 'right';
  collapsible?: boolean;
  collapsed?: boolean;
}

const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  ({ 
    className, 
    children, 
    width = 'md', 
    position = 'left',
    collapsible = false,
    collapsed = false,
    ...props 
  }, ref) => {
    const getWidthClasses = () => {
      if (collapsed && collapsible) return 'w-16';
      
      const widthClasses = {
        sm: 'w-48',
        md: 'w-64',
        lg: 'w-80',
      };
      
      return widthClasses[width];
    };

    return (
      <aside
        ref={ref}
        className={cn(
          'h-screen bg-gray-50 border-gray-200 transition-all duration-200',
          getWidthClasses(),
          position === 'left' ? 'border-r' : 'border-l',
          className
        )}
        {...props}
      >
        {children}
      </aside>
    );
  }
);

Sidebar.displayName = 'Sidebar';

// Footer Component
export interface FooterProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  sticky?: boolean;
  bordered?: boolean;
}

const Footer = forwardRef<HTMLElement, FooterProps>(
  ({ className, children, sticky = false, bordered = true, ...props }, ref) => {
    return (
      <footer
        ref={ref}
        className={cn(
          'w-full bg-white dark:bg-gray-900 mt-auto',
          sticky && 'sticky bottom-0',
          bordered && 'border-t border-gray-200',
          className
        )}
        {...props}
      >
        {children}
      </footer>
    );
  }
);

Footer.displayName = 'Footer';

// Container for content width
export interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  padding?: boolean;
}

const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({ className, children, size = 'xl', padding = true, ...props }, ref) => {
    const getSizeClasses = () => {
      const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-7xl',
        '2xl': 'max-w-full',
        full: 'w-full',
      };
      
      return sizeClasses[size];
    };

    return (
      <div
        ref={ref}
        className={cn(
          'mx-auto w-full',
          getSizeClasses(),
          padding && 'px-4 sm:px-6 lg:px-8',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Container.displayName = 'Container';

// Grid Layout
export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  responsive?: boolean;
}

const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({ className, children, cols = 1, gap = 'md', responsive = true, ...props }, ref) => {
    const getGridClasses = () => {
      const baseClasses = 'grid';
      
      const colClasses = {
        1: 'grid-cols-1',
        2: responsive ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2',
        3: responsive ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-3',
        4: responsive ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-4',
        5: responsive ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-5',
        6: responsive ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-6' : 'grid-cols-6',
        12: 'grid-cols-12',
      };
      
      const gapClasses = {
        sm: 'gap-2',
        md: 'gap-4',
        lg: 'gap-6',
        xl: 'gap-8',
      };
      
      return cn(baseClasses, colClasses[cols], gapClasses[gap]);
    };

    return (
      <div
        ref={ref}
        className={cn(getGridClasses(), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Grid.displayName = 'Grid';

export { 
  Layout, 
  Header, 
  Main, 
  Sidebar, 
  Footer, 
  Container, 
  Grid 
};
